import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getRecentSearches, clearRecentSearches } from '../../services'
import CloseIcons from '@/icons/CloseIcons'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface RecentSearchesProps {
    onSearchClick: (query: string) => void
    onClearAll?: () => void
    className?: string
    maxItems?: number
    showHeader?: boolean
    showClearAll?: boolean
}

const RecentSearches: React.FC<RecentSearchesProps> = ({
    onSearchClick,
    onClearAll,
    className = '',
    maxItems = 10,
    showHeader = true,
    showClearAll = true,
}) => {
    const { t } = useTranslation('search')
    const queryClient = useQueryClient()

    const {
        data: recentSearches = [],
        isLoading,
        error,
    } = useQueryWithHandler<string[]>(
        {
            queryKey: [QUERY_KEYS.SEARCH_RECENT_SEARCHES],
            queryFn: getRecentSearches,
            staleTime: 1000 * 60 * 10, // 10 minutes
            retry: 1, // Only retry once
        },
        { showLoading: false, showError: false }
    )

    console.log('🔍 RecentSearches Debug:', {
        recentSearches,
        isLoading,
        error,
    })

    const handleClearAll = async () => {
        try {
            await clearRecentSearches()
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SEARCH_RECENT_SEARCHES],
            })
            if (onClearAll) {
                onClearAll()
            }
        } catch (error) {
            console.error('Error clearing recent searches:', error)
        }
    }

    if (isLoading) {
        console.log('🔄 RecentSearches: Loading...')
        return (
            <div className={`p-4 ${className}`}>
                <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="space-y-1">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="h-8 bg-gray-100 rounded"
                            ></div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    // Show nothing if error or no data
    if (error || recentSearches.length === 0) {
        return null
    }

    const displayItems = recentSearches.slice(0, maxItems)

    return (
        <div className={`${className}`}>
            {showHeader && (
                <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <Typogaphy className="text-sm font-medium text-gray-700">
                            {t('recent-searches')} ({recentSearches.length})
                        </Typogaphy>
                    </div>
                    {showClearAll && recentSearches.length > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleClearAll}
                            className="text-xs"
                        >
                            {t('clear-all')}
                        </Button>
                    )}
                </div>
            )}

            <div className="divide-y divide-gray-100">
                {displayItems.map((searchQuery, index) => (
                    <div
                        key={index}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors group"
                    >
                        <button
                            onClick={() => onSearchClick(searchQuery)}
                            className="flex items-center gap-3 flex-1 text-left"
                        >
                            <svg
                                className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                            <Typogaphy className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                                {searchQuery}
                            </Typogaphy>
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                // TODO: Implement remove individual search
                                console.log('Remove search:', searchQuery)
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
                        >
                            <CloseIcons className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                        </button>
                    </div>
                ))}
            </div>

            {recentSearches.length > maxItems && (
                <div className="p-3 text-center border-t border-gray-100">
                    <Typogaphy className="text-xs text-gray-500">
                        {t('and-more-searches', {
                            count: recentSearches.length - maxItems,
                        })}
                    </Typogaphy>
                </div>
            )}
        </div>
    )
}

export default RecentSearches
